import React, { createContext, useContext, useState, useEffect } from 'react';
import * as staticData from '../data';
import Loader from '../components/Loader';
import { client } from '../lib/sanity';
import { mergePortfolioItem, mergeServiceDetail } from '../lib/normalizeServiceDetail';

const CMSContext = createContext(null);

export function CMSProvider({ children }) {
  const [data, setData] = useState(staticData);
  const [loading, setLoading] = useState(true);
  const [loaderComplete, setLoaderComplete] = useState(false);

  useEffect(() => {
    const projectId = import.meta.env.VITE_SANITY_PROJECT_ID;
    if (!projectId) {
      console.log('Sanity Project ID not set. Operating in static data mode.');
      setData(staticData);
      setLoading(false);
      return;
    }

    const fetchCMSData = async () => {
      try {
        const sanityData = await client.fetch(`{
          "homeData": *[_type == "home"][0],
          "servicesData": *[_type == "servicesPage"][0],
          "globalData": *[_type == "globalSettings"][0],
          "services": *[_type == "service"] | order(num asc) {
            ...,
            detailData {
              ...,
              split {
                ...,
                "image": coalesce(image.asset->url, image)
              }
            }
          },
          "teamMembers": *[_type == "teamMember"] | order(order asc) {
            ...,
            "photo": select(
              defined(photo.asset->url) => photo.asset->url + "?auto=format&q=95&w=1000",
              photo
            )
          },
          "blogPosts": *[_type == "blogPost"] {
            ...,
            "image": coalesce(image.asset->url, image)
          },
          "portfolioItems": *[_type == "portfolioItem"] | order(num asc) {
            ...,
            gallery[] {
              ...,
              "image": coalesce(image.asset->url, image),
              "images": coalesce(images[].asset->url, images),
              "videoFile": videoFile.asset->url,
              "video": coalesce(videoFile.asset->url, videoUrl),
              "websiteUrl": coalesce(websiteUrl, link, url),
              "buttonText": buttonText,
              "link": coalesce(websiteUrl, link, url)
            }
          },
          "contactData": *[_type == "contactData"][0]
        }`);

        // Merge Sanity service detailData with static fallbacks using deep merge + normalization
        const servicesDetailMap = {};
        if (sanityData.services?.length > 0) {
          sanityData.services.forEach(s => {
            if (s.detailData) {
              servicesDetailMap[s.slug.current] = mergeServiceDetail(
                staticData.servicesDetailData[s.slug.current] || {},
                s.detailData
              );
            }
          });
        }

        const staticPortfolioByNum = Object.fromEntries(
          (staticData.portfolioData.items || []).map((item) => [item.num, item])
        );
        const mergedPortfolioItems = (sanityData.portfolioItems && sanityData.portfolioItems.length > 0)
          ? sanityData.portfolioItems.map((item) =>
            mergePortfolioItem(staticPortfolioByNum[item.num] || {}, item)
          )
          : staticData.portfolioData.items;

        // Merge Sanity data on top of static data fallbacks
        const mergedData = {
          ...staticData,
          globalData: {
            ...staticData.globalData,
            ...(sanityData.globalData || {}),
            footer: {
              ...staticData.globalData.footer,
              socials: (sanityData.contactData?.socials && sanityData.contactData.socials.length > 0)
                ? sanityData.contactData.socials
                : staticData.globalData.footer.socials,
              contact: sanityData.contactData
                ? {
                  email: sanityData.contactData.email || staticData.globalData.footer.contact?.email,
                  phone: sanityData.contactData.phone || staticData.globalData.footer.contact?.phone,
                  location: sanityData.contactData.location || staticData.globalData.footer.contact?.location,
                  locationLink: sanityData.contactData.locationLink || staticData.globalData.footer.contact?.locationLink || '#'
                }
                : staticData.globalData.footer.contact
            }
          },
          homeData: {
            ...staticData.homeData,
            ...(sanityData.homeData || {}),
            teamList: (sanityData.teamMembers && sanityData.teamMembers.length > 0)
              ? sanityData.teamMembers
              : staticData.homeData.teamList
          },
          servicesData: {
            ...staticData.servicesData,
            ...(sanityData.servicesData || {}),
            servicesGrid: (sanityData.services && sanityData.services.length > 0)
              ? sanityData.services.map(s => ({
                num: s.num,
                title: s.title,
                link: s.link,
                description: s.description,
                bullets: s.bullets
              }))
              : staticData.servicesData.servicesGrid
          },
          servicesDetailData: {
            ...staticData.servicesDetailData,
            ...servicesDetailMap
          },
          teamData: {
            ...staticData.teamData,
            teamList: (sanityData.teamMembers && sanityData.teamMembers.length > 0)
              ? sanityData.teamMembers
              : staticData.teamData.teamList
          },
          blogsDetailData: (sanityData.blogPosts && sanityData.blogPosts.length > 0)
            ? sanityData.blogPosts
            : staticData.blogsDetailData,
          portfolioData: {
            ...staticData.portfolioData,
            items: mergedPortfolioItems
          },
          contactData: {
            ...staticData.contactData,
            details: {
              ...staticData.contactData.details,
              items: sanityData.contactData
                ? [
                  { label: 'Email', val: sanityData.contactData.email, type: 'email' },
                  { label: 'Phone', val: sanityData.contactData.phone, type: 'phone' },
                  { label: 'Location', val: sanityData.contactData.location, type: 'text' },
                  { label: 'WhatsApp', val: 'Connect With Us', type: 'link', href: sanityData.contactData.whatsapp }
                ]
                : staticData.contactData.details.items
            }
          }
        };

        setData(mergedData);
      } catch (err) {
        console.warn('Failed to fetch from Sanity. Falling back to local static data.', err);
        setData(staticData);
      } finally {
        setLoading(false);
      }
    };

    fetchCMSData();
  }, []);

  const brandName = (data.globalData?.logo?.text && data.globalData?.logo?.boldText)
    ? `${data.globalData.logo.text}${data.globalData.logo.boldText}`
    : (data.globalData?.logo?.text || 'AZAZIYA');

  const brandData = {
    brandName,
    brandTagline: data.homeData?.hero?.tagline || 'We Design The Unseen'
  };

  return (
    <CMSContext.Provider value={data}>
      {(!loaderComplete || loading) && (
        <Loader
          brandData={brandData}
          onComplete={() => setLoaderComplete(true)}
        />
      )}
      {children}
    </CMSContext.Provider>
  );
}

export function useCMS() {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within a CMSProvider');
  }
  return context;
}
